import {
	action,
	autorun,
	computed,
	observable,
	makeObservable,
	runInAction,
	reaction,
} from 'mobx';
import {enumToValues} from '@netx/core-next/utils/enum';
import {RuleModel} from '../baseModel';
import {VisualType, SearchTypeVisual, TSearchTypeVisual} from './constants';
import {visualRuleDefaults} from './defaults';
import type {VisualRule} from './types';
import {validateVisualRule} from './validate';
import {getAssetBean} from '@netx/core-asset-next/api/getAssetBean';

export class VisualRuleModel
	extends RuleModel<TSearchTypeVisual>
	implements Omit<VisualRule, 'elementValue1'>
{
	readonly elementType = SearchTypeVisual;
	elementSubType1: VisualType = visualRuleDefaults.elementSubType1;
	elementSubType2: 0 = visualRuleDefaults.elementSubType2;
	elementValue1: number | string = visualRuleDefaults.elementValue1;
	elementValue2: '' = visualRuleDefaults.elementValue2;
	elementValue3: '' = visualRuleDefaults.elementValue3;

	get elementSubType1Options() {
		return enumToValues(VisualType);
	}

	get isValid() {
		const validation = validateVisualRule(this as VisualRule);
		this.setValidationErrors(this.dirty && validation ? validation : undefined);
		if (!validation) return true;
		return false;
	}

	constructor(params?: Partial<Omit<VisualRule, 'elementType'>>) {
		super();

		runInAction(() => {
			Object.assign(this, {...visualRuleDefaults, ...params});
		});

		makeObservable(this, {
			elementSubType1: observable,
			elementSubType1Options: computed,
			elementValue1: observable,
			isValid: computed,
			setElementSubType1: action,
			setElementValue1: action,
		});

		this.reactionDisposers.push(
			reaction(
				() => this.elementSubType1,
				(elementSubType1) => {
					this.elementValue1 =
						elementSubType1 === VisualType.COLOR ? '#000000' : 0;
					this.dirty = false;
					if (elementSubType1 === VisualType.COLOR) {
						this.setElementValue1Label('');
						this.setElementValue1Unavailable(false);
					}
				},
			),
			autorun(() => {
				if (this.elementSubType1 === VisualType.COLOR) return;
				this.setElementValue1Unavailable(false);

				if (!this.elementValue1) return;
				this.setElementValue1Loading(true);
				getAssetBean({id: this.elementValue1 as number})
					.then((asset) => this.setElementValue1Label(asset.file))
					.catch(() => this.setElementValue1Unavailable(true))
					.finally(() => this.setElementValue1Loading(false));
			}),
		);
	}

	/**
	 * Set whether this is an asset or color visual search
	 * @param elementSubType1 VisualType
	 */
	setElementSubType1(elementSubType1: VisualType) {
		this.elementSubType1 = elementSubType1;
	}

	/** stubbed */
	setElementSubType2() {}

	/**
	 * Set search value
	 * @param elementValue1 hex color string | numerical asset id
	 */
	setElementValue1<T extends VisualType = VisualType>(
		elementValue1: [T] extends [VisualType.ASSET] ? number : string,
	) {
		this.elementValue1 = elementValue1 as this['elementValue1'];
		this.dirty = true;
	}

	/** stubbed */
	setElementValue2() {}

	/** stubbed */
	setElementValue3() {}

	toRuleObject(): VisualRule {
		return {
			elementType: this.elementType,
			elementSubType1: this.elementSubType1,
			elementSubType2: this.elementSubType2,
			elementValue1:
				this.elementSubType1 === VisualType.COLOR
					? // we need to strip this for the server
					  this.elementValue1.toString().replace('#', '')
					: (this.elementValue1 as number),
			elementValue2: this.elementValue2,
			elementValue3: this.elementValue3,
		} as VisualRule<typeof this.elementSubType1>;
	}
}

export default VisualRuleModel;
